import React, { useState } from 'react';
import './BulkActionsBar.css';

/**
 * Barre d'actions pour les documents sélectionnés
 * Positionnée en bas de l'écran avec un style moderne
 */
const BulkActionsBar = ({ selectedCount, onAction }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  
  if (selectedCount === 0) return null;
  
  return (
    <div 
      className="animate-slide-up"
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        maxWidth: '90vw'
      }}
    >
      <div 
        className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-full shadow-2xl px-6 py-3 flex items-center gap-4 flex-wrap"
        style={{
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* Compteur */}
        <div className="flex items-center gap-2 border-r border-emerald-400 pr-4">
          <div className="w-8 h-8 bg-white text-emerald-600 rounded-full flex items-center justify-center font-bold text-sm">
            {selectedCount}
          </div>
          <span className="text-sm font-medium">
            {selectedCount === 1 ? 'document sélectionné' : 'documents sélectionnés'}
          </span>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Changer statut */}
          <div className="relative">
            <button
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              className="px-4 py-2 bg-white text-emerald-600 rounded-full text-sm font-medium hover:bg-emerald-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Changer statut
              <svg className={`w-4 h-4 transition-transform ${showStatusMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showStatusMenu && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px]">
                <button
                  onClick={() => { onAction('status', 'envoyé'); setShowStatusMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                >
                  <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  Envoyé
                </button>
                <button
                  onClick={() => { onAction('status', 'acceptée'); setShowStatusMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 flex items-center gap-2"
                >
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  Accepté
                </button>
                <button
                  onClick={() => { onAction('status', 'refusée'); setShowStatusMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-red-50 flex items-center gap-2"
                >
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  Refusé
                </button>
                <button
                  onClick={() => { onAction('status', 'annulé'); setShowStatusMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center gap-2"
                >
                  <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                  Annulé
                </button>
              </div>
            )}
          </div>
          
          {/* Envoyer par email */}
          <button
            onClick={() => onAction('email')}
            className="px-4 py-2 bg-white text-emerald-600 rounded-full text-sm font-medium hover:bg-emerald-50 transition-colors flex items-center gap-2"
            title="Envoyer par email"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Envoyer
          </button>
          
          {/* Télécharger */}
          <button
            onClick={() => onAction('download')}
            className="px-4 py-2 bg-white text-emerald-600 rounded-full text-sm font-medium hover:bg-emerald-50 transition-colors flex items-center gap-2"
            title="Télécharger en ZIP"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Télécharger
          </button>
          
          {/* Exporter */}
          <button
            onClick={() => onAction('export')}
            className="px-4 py-2 bg-white text-emerald-600 rounded-full text-sm font-medium hover:bg-emerald-50 transition-colors flex items-center gap-2"
            title="Exporter en CSV"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exporter
          </button>
          
          {/* Supprimer */}
          <button
            onClick={() => onAction('delete')}
            className="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
            title="Supprimer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
        
        {/* Fermer */}
        <button
          onClick={() => onAction('deselect_all')}
          className="ml-2 p-2 hover:bg-emerald-500 rounded-full transition-colors"
          title="Désélectionner tout"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BulkActionsBar;
